import React, { useState, useRef, useEffect } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { FaCalendarCheck, FaUserPlus } from "react-icons/fa";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import "../styles/doctorProfile.css";
import InviteModal from '../components/InviteModal';
import BookAppointmentModal from './BookAppointmentModal';
import LoginModal from '../components/LoginModal';

const Profile = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { doctorId } = useParams();
  const doctor = location.state?.doctorData;

  // Reveal state for View All
  const [showAll, setShowAll] = useState(false);
  const contentRef = useRef(null);

  // Modal states
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [bookAppointmentModalOpen, setBookAppointmentModalOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [clinicId, setClinicId] = useState(null);


  if (!doctor) {
    return (
      <div>
        <Navbar />
        <div className="profile-fallback">
          <h2>Doctor profile not found.</h2>
          <button className="profile-back-btn" onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  // Helper for rendering lists
  const renderList = (val, className = "") => {
    if (Array.isArray(val) && val.length) {
      return (
        <ul className={className}>
          {val.map((item, idx) => (
            <li key={idx}>
              <span className="profile-dot" /> {item}
            </li>
          ))}
        </ul>
      );
    } else if (typeof val === "string" && val.trim() !== "") {
      return (
        <ul className={className}>
          <li>
            <span className="profile-dot" /> {val}
          </li>
        </ul>
      );
    } else {
      return <span className="profile-empty">-</span>;
    }
  };

  // Compose address from locations array or fallback
  const address = doctor.locations && doctor.locations.length
    ? `${doctor.locations[0].address || ''}${doctor.locations[0].address ? ', ' : ''}${doctor.locations[0].city || ''}${doctor.locations[0].city ? ', ' : ''}${doctor.locations[0].state || ''}${doctor.locations[0].state ? ', ' : ''}${doctor.locations[0].country || ''}${doctor.locations[0].postal_code ? ', ' + doctor.locations[0].postal_code : ''}`.replace(/, $/, '')
    : (doctor.address || "N/A");

  // Compose registration info
  const registrationBody = doctor.registration?.body || "General Medical Council";
  const registrationNumber = doctor.registration?.number || "N/A";

  // Compose expertise
  const expertise = doctor.areas_of_expertise;

  // Compose specialties
  const specialties = doctor.subspecialties || doctor.specialties;

  // Compose conditions & treatments
  const conditions = doctor.conditions_and_treatments;

  // Compose qualifications
  const qualifications = doctor.qualifications || doctor.degrees;

  // Compose diseases & treatments (horizontal list)
  const diseases = doctor.diseases_and_treatments;

  // Improved skill endorsements with endorser
  const skillEndorsements = Array.isArray(doctor.skill_endorsements)
    ? doctor.skill_endorsements.map(
      (endorsement) =>
        endorsement && endorsement.skill
          ? `${endorsement.skill}${endorsement.endorsed_by && endorsement.endorsed_by.length ? ` - Endorsed by ${endorsement.endorsed_by.join(", ")}` : ""} (${endorsement.count} time${endorsement.count === 1 ? '' : 's'})`
          : ""
    )
    : [""];

  // Compose languages
  const languages = doctor.languages_spoken;

  // Compose insurers
  const insurers = doctor.insurers;

  // Compose gender, dob, experience, fees, connections, verified
  const gender = doctor.gender || "N/A";
  const dob = doctor.date_of_birth || "N/A";
  const experience = doctor.experience_years !== undefined ? doctor.experience_years : "N/A";
  const newFee = doctor.new_appointment_fee !== undefined ? `£${doctor.new_appointment_fee}` : "Not Available";
  const followUpFee = doctor.follow_up_fee !== undefined ? `£${doctor.follow_up_fee}` : "Not Available";
  const connections = doctor.healthcare_connections !== undefined ? doctor.healthcare_connections : "N/A";
  const verified = doctor.verified ? "Yes" : "No";

  // For smooth transition, set maxHeight to a value that covers about half the content
  const collapsedHeight = 520; // px, adjust as needed

  const handleLoginSuccess = async () => {
    try {
      const authToken = localStorage.getItem("authToken");
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");

      if (!authToken) {
        console.error("No auth token found after login");
        return;
      }

      // Make the booking API call
      const response = await fetch("https://staging.virtualtriage.ai/api/v1/patient/appointment/book-appointment/directory/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": `Bearer ${authToken}`
        },
        body: JSON.stringify({
          doctor_email: doctor.email
        }),
      });

      const data = await response.json();
      console.log("API Response after login:", data);

      if (data.status === false && data.clinic_id) {
        // Redirect to the website's clinic details page
        navigate(`/clinic-details/${data.clinic_id}`);
      } else {
        alert(data.message || "Failed to book appointment.");
      }
    } catch (error) {
      console.error("Error booking appointment after login:", error);
      alert("Error booking appointment. Please try again later.");
    }
  };

  const handleBookAppointment = async () => {
    try {
      const authToken = localStorage.getItem("authToken");

      if (!authToken) {
        console.log("Not logged in, showing login modal");
        setShowLoginModal(true);
        return;
      }

      // If logged in, proceed with booking
      handleLoginSuccess();
    } catch (error) {
      console.error("Error in handleBookAppointment:", error);
      alert("Error booking appointment. Please try again later.");
    }
  };

  return (
    <div>
      <Navbar />
      <div className="background-gradient-color"></div>
      <div className="profile-container" style={{ position: 'relative' }}>
        {/* Doctor Name (always visible) */}
        <h2 className="profile-name profile-unblur">{doctor.full_name}</h2>
        {/* Invite Section (always visible) */}
        <InviteModal
          isOpen={inviteModalOpen}
          onClose={() => setInviteModalOpen(false)}
          doctorName={doctor.full_name}
          doctorEmail={doctor.email}
        />
        {/* Main Content (blurred if not verified) */}
        <div className={doctor.verified ? "" : "profile-blur"}>
          <div
            className={`profile-content-reveal${showAll ? ' expanded' : ''}`}
            style={{
              maxHeight: showAll ? '2000px' : `${collapsedHeight}px`,
              overflow: showAll ? 'visible' : 'hidden',
              transition: 'max-height 0.7s cubic-bezier(0.4,0,0.2,1)'
            }}
            ref={contentRef}
          >
            <div className="profile-card">
              {/* Left Column */}
              <div className="profile-left">
                <div className="profile-img-wrap">
                  <img
                    src={doctor.profile_picture_url || process.env.PUBLIC_URL + "/assets/dummy-profile.png"}
                    alt={doctor.full_name}
                    className="profile-img"
                    onError={e => { e.target.onerror = null; e.target.src = process.env.PUBLIC_URL + "/assets/dummy-profile.png"; }}
                  />
                </div>
                <div className="profile-specialty">{specialties[0] || doctor.specialty || "General Practitioner"}</div>
                <div className="profile-rating">
                  Ratings: {" "}
                  <span className="profile-stars">
                    {[1, 2, 3, 4, 5].map((i) =>
                      i <= Math.round(doctor.rating || 0) ? "★" : "☆"
                    )}
                  </span>
                  {doctor.rating && (
                    <span style={{ marginLeft: 8, color: '#888', fontSize: '1rem' }}>({doctor.rating.toFixed(1)})</span>
                  )}
                </div>
                <div className="profile-section">
                  <h3>About Me</h3>
                  <p>{doctor.about || doctor.about_me || "No bio available."}</p>
                </div>
                <div className="profile-section">
                  <h3>Registration</h3>
                  {renderList([
                    `Registered With: ${registrationBody || "N/A"}`,
                    `Registration Number/ID: ${registrationNumber || "N/A"}`
                  ])}
                </div>
                <div className="profile-section">
                  <h3>Insurers</h3>
                  {renderList(insurers)}
                </div>
                <div className="profile-section">
                  <h3>Qualification</h3>
                  {renderList(qualifications)}
                </div>
                <div className="profile-section">
                  <h3>Languages Spoken</h3>
                  {renderList(languages)}
                </div>
              </div>
              {/* Right Column */}
              <div className="profile-right">
                <div className="profile-section">
                  <h3>Contact</h3>
                  {renderList([
                    `Address: ${address}`
                  ])}
                  <button className="profile-reveal-btn"
                    onClick={() => window.location.href = `mailto:${doctor.email}`}
                    disabled={!doctor.verified}
                    style={!doctor.verified ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                  >
                    Contact
                  </button>
                </div>
                <div className="profile-section">
                  <h3>Personal Info</h3>
                  <ul>
                    <li><span className="profile-dot" />Gender: {gender}</li>
                    <li><span className="profile-dot" />Date of Birth: {dob}</li>
                    <li><span className="profile-dot" />Experience: {experience} years</li>
                    <li><span className="profile-dot" />Verified: {verified}</li>
                  </ul>
                </div>
                <div className="profile-section">
                  <h3>Specialties</h3>
                  {renderList(specialties)}
                </div>
                <div className="profile-section">
                  <h3>Areas of Expertise</h3>
                  {renderList(expertise)}
                </div>
                <div className="profile-section">
                  <h3>Conditions & Treatments</h3>
                  {renderList(conditions)}
                </div>
                {/* Diseases & Treatments horizontal list */}
                <div className="profile-horizontal-section">
                  <h3>Diseases & Treatments</h3>
                  {renderList(diseases, "profile-horizontal-list")}
                </div>
              </div>
            </div>
            {/* Gradient overlay and View All button (moved inside) */}
            {!showAll && (
              <div className="profile-reveal-overlay">
                <div className="profile-reveal-gradient" />
                <button className="profile-reveal-btn" onClick={() => setShowAll(true)}>
                  View All
                </button>
              </div>
            )}
          </div>
        </div>
          {/* Consultation Fees always visible */}
          <div className="profile-section-1" style={{ filter: 'none !important' }}>
            <div className="profile-fees-section">
              <h3>Consultation Fees</h3>
              <ul>
                <li><span className="profile-dot" /> New Appointment:&nbsp;<strong>{newFee}</strong></li>
                <li><span className="profile-dot" /> Follow-up:&nbsp;<strong>{followUpFee}</strong></li>
              </ul>
              {doctor.status === false ? (
                <button className="profile-appointment-btn" onClick={() => setInviteModalOpen(true)}><FaUserPlus style={{ marginRight: 8 }} />Invite</button>
              ) : null}
              {doctor.status !== false ? (
                <button className="profile-appointment-btn" onClick={handleBookAppointment}><FaCalendarCheck /> Book Appointment</button>
              ) : null}
            </div>
          </div>
      </div>
      <section className="profile-cta">
        <div className="cta-container">
          <h2 className="cta-title">Your Healthcare, Your Terms</h2>
          <p>Whether it's a late-night consultation, a second opinion, or urgent medical advice, Virtual Triage ensures that healthcare is always within your reach.</p>
          <a href="https://virtualtriage.co/home-page" style={{ textDecoration: 'none' }}>
            <button className="why-listing-btn" style={{ alignSelf: 'center' }}>Get Started Now!</button>
          </a>
        </div>
      </section>
      <BookAppointmentModal
        isOpen={bookAppointmentModalOpen}
        onClose={() => setBookAppointmentModalOpen(false)}
        doctorName={doctor.full_name}
        doctorEmail={doctor.email}
      />
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => {
          console.log("Closing login modal");
          setShowLoginModal(false);
        }}
        onLoginSuccess={handleLoginSuccess}
      />
      <Footer />
    </div>
  );
};

export default Profile;
